import { Anvil, Brain, Dumbbell, Pickaxe, Recycle, Sword, TreePine } from "lucide-react";
import {
  type EquipmentItem,
  type InventoryView,
  type LifeProfessionId,
  type LifeProfile,
  type MeResponse,
} from "../types";
import { formatDisplayNumber } from "../ui/format";

export interface LifeMetric {
  label: string;
  value: string;
}

export function lifeProfessionBenefitLabel(profession: LifeProfile["professions"][number]): string {
  if (profession.benefitKind === undefined || profession.benefitBasisPoints === undefined) {
    switch (profession.id) {
      case "enhancement":
        return "成功率见所选装备";
      case "research":
        return "结晶发现率成长";
      case "fitness":
        return "生命/防御成长";
      case "training":
        return "攻击成长";
    }
  }
  const points = profession.benefitBasisPoints ?? profession.speedBonusPercent * 100;
  const value = formatDisplayNumber(points / 100);
  switch (profession.benefitKind) {
    case "enhancement_success":
      return `成功 +${value} 个百分点`;
    case "research_crystal_chance":
      return `发现 ${value}%`;
    case "fitness_attributes":
      return `生命/防御 +${value}%`;
    case "training_attack":
      return `攻击 +${value}%`;
    default:
      return `速度 +${value}%`;
  }
}

export function lifeProfessionCompactBenefit(
  profession: LifeProfile["professions"][number],
): string {
  if (profession.benefitKind === undefined || profession.benefitBasisPoints === undefined) {
    switch (profession.id) {
      case "enhancement":
        return "提高成功率";
      case "research":
        return "提高发现率";
      case "fitness":
        return "生命/防御";
      case "training":
        return "攻击成长";
    }
  }
  const points = profession.benefitBasisPoints ?? profession.speedBonusPercent * 100;
  const value = formatDisplayNumber(points / 100);
  switch (profession.benefitKind) {
    case "enhancement_success":
      return `成功 +${value}点`;
    case "research_crystal_chance":
      return `发现 ${value}%`;
    case "fitness_attributes":
      return `生命/防御 +${value}%`;
    case "training_attack":
      return `攻击 +${value}%`;
    default:
      return `速度 +${value}%`;
  }
}

export function lifeNextBenefitLabel(profession: LifeProfile["professions"][number]): string {
  if (profession.benefitKind === undefined) return "继续工作提升";
  if (!profession.nextBenefitLevel || profession.nextBenefitBasisPoints === undefined)
    return "已接近上限";
  const next = formatDisplayNumber(profession.nextBenefitBasisPoints / 100);
  switch (profession.benefitKind) {
    case "enhancement_success":
      return `Lv.${profession.nextBenefitLevel}：成功 +${next}点`;
    case "research_crystal_chance":
      return `Lv.${profession.nextBenefitLevel}：发现 ${next}%`;
    case "fitness_attributes":
      return `Lv.${profession.nextBenefitLevel}：生命/防御 +${next}%`;
    case "training_attack":
      return `Lv.${profession.nextBenefitLevel}：攻击 +${next}%`;
    default:
      return `Lv.${profession.nextBenefitLevel}：速度 +${next}%`;
  }
}

export function lifeProfessionMetrics(
  profession: LifeProfile["professions"][number],
  profile: LifeProfile,
  inventory: InventoryView,
  me: MeResponse,
  selectedItem: EquipmentItem | undefined,
  primaryAffix: string,
  secondaryAffix: string,
): LifeMetric[] {
  const metrics: LifeMetric[] = [
    { label: "基础循环", value: `${profession.cycleSeconds}s` },
    { label: "当前收益", value: lifeProfessionBenefitLabel(profession) },
  ];
  if (profession.id === "gathering") {
    metrics.push({ label: "木材余额", value: `${me.player.wood}/${me.player.storageCaps.wood}` });
    metrics.push({
      label: "可用仓位",
      value: `${Math.max(0, me.player.storageCaps.wood - me.player.wood)}`,
    });
  } else if (profession.id === "mining") {
    metrics.push({ label: "矿材余额", value: `${me.player.ore}/${me.player.storageCaps.ore}` });
    metrics.push({
      label: "可用仓位",
      value: `${Math.max(0, me.player.storageCaps.ore - me.player.ore)}`,
    });
  } else if (profession.id === "conversion") {
    metrics.push({
      label: "可转次数",
      value: `${Math.floor(Math.min(me.player.wood, me.player.ore) / 20)}`,
    });
    metrics.push({ label: "结晶余额", value: `${me.player.crystal}` });
  } else if (profession.id === "enhancement") {
    metrics.push({ label: "下一收益", value: lifeNextBenefitLabel(profession) });
    metrics.push({ label: "金币余额", value: `${me.player.gold} 金` });
  } else if (profession.id === "research") {
    metrics.push({ label: "下一收益", value: lifeNextBenefitLabel(profession) });
    metrics.push({
      label: "目标研究",
      value: `${lifeResearchExperience(profile, primaryAffix, "primary")}/${lifeResearchExperience(profile, secondaryAffix, "secondary")} XP`,
    });
  } else if (profession.id === "fitness") {
    metrics.push({ label: "下一收益", value: lifeNextBenefitLabel(profession) });
    metrics.push({ label: "当前最大生命", value: `${inventory.stats.maxHp}` });
  } else if (profession.id === "training") {
    metrics.push({ label: "下一收益", value: lifeNextBenefitLabel(profession) });
    metrics.push({ label: "当前攻击", value: `${inventory.stats.attack}` });
  }
  return metrics;
}

export function lifeResearchExperience(
  profile: LifeProfile,
  affixId: string,
  kind: "primary" | "secondary",
): number {
  const values = kind === "primary" ? profile.primaryProbabilities : profile.secondaryProbabilities;
  return values.find((entry) => entry.affixId === affixId)?.researchExperience ?? 0;
}

export function ProbabilityColumn({
  title,
  values,
  focused,
}: {
  title: string;
  values: LifeProfile["primaryProbabilities"];
  focused: string;
}) {
  return (
    <section>
      <h3>{title}</h3>
      <div>
        {values.map((entry) => (
          <span
            key={entry.affixId}
            className={entry.affixId === focused ? "is-focused" : ""}
            title={`精确概率 ${entry.exactPercent}% · 研究经验 ${entry.researchExperience}`}
          >
            <b>{entry.name}</b>
            <span className="life-probability-value">
              <em>{entry.lessThanFive ? "<5%" : `${entry.approxPercent}%`}</em>
              <small>{entry.researchExperience} XP</small>
            </span>
          </span>
        ))}
      </div>
    </section>
  );
}

export function LifeProfessionIcon({ id, size = 20 }: { id: LifeProfessionId; size?: number }) {
  if (id === "gathering") return <TreePine size={size} />;
  if (id === "mining") return <Pickaxe size={size} />;
  if (id === "conversion") return <Recycle size={size} />;
  if (id === "enhancement") return <Anvil size={size} />;
  if (id === "research") return <Brain size={size} />;
  if (id === "fitness") return <Dumbbell size={size} />;
  return <Sword size={size} />;
}
